import React, { useId, useState } from "react";
import { Upload, X } from "lucide-react";
import { formatFileSize, getFileIcon, splitFileName } from "../AttachmentUI";
import { mergeFiles } from "./attachments";

interface AttachmentDropzoneProps {
  files: File[];
  onChange: (files: File[]) => void;
  /** What kind of files help this request, e.g. "screenshots or logs". */
  hint?: string;
}

/**
 * Drop files here or browse for them. Every pick adds to the list instead of
 * replacing it, as the old "Choose Files" input did, and each file can be
 * removed on its own. The rows match the attachments in the ticket detail
 * (CTX-1121), so a file looks the same before and after it is sent.
 */
export function AttachmentDropzone({
  files,
  onChange,
  hint = "Screenshots, logs or any file that helps us understand the request.",
}: AttachmentDropzoneProps) {
  const inputId = useId();
  const [isDragging, setIsDragging] = useState(false);

  const addFiles = (picked: FileList | null) => {
    if (!picked || picked.length === 0) return;
    onChange(mergeFiles(files, Array.from(picked)));
  };

  const removeFile = (index: number) =>
    onChange(files.filter((_, i) => i !== index));

  return (
    <div className="space-y-2">
      <label
        htmlFor={inputId}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragging(false);
          addFiles(e.dataTransfer.files);
        }}
        className={`
          flex cursor-pointer flex-col items-center gap-1 rounded-md border border-dashed px-4 py-5
          text-center transition-colors
          ${
            isDragging
              ? "border-semantic-border-brand-primary bg-semantic-bg-brand-secondary"
              : "border-semantic-border-secondary bg-semantic-bg-primary hover:border-semantic-border-brand-primary"
          }
        `}
      >
        <Upload className="h-5 w-5 text-semantic-text-brand" aria-hidden />
        {/* A phone has nothing to drop: its picker offers files and the
            camera (CTX-1433). */}
        <span className="text-sm text-semantic-text-primary sm:hidden">
          <span className="font-semibold text-semantic-text-brand">
            Choose a file
          </span>{" "}
          or take a photo
        </span>
        <span className="hidden text-sm text-semantic-text-primary sm:inline">
          <span className="font-semibold text-semantic-text-brand">
            Browse files
          </span>{" "}
          or drop them here
        </span>
        <span className="text-xs text-semantic-text-secondary">{hint}</span>
        <input
          id={inputId}
          type="file"
          multiple
          className="sr-only"
          onChange={(e) => {
            addFiles(e.target.files);
            // Lets the same file be picked again after removing it.
            e.target.value = "";
          }}
        />
      </label>

      {files.length > 0 && (
        <ul className="space-y-1.5" aria-label="Attached files">
          {files.map((file, index) => (
            <AttachedFileRow
              key={`${file.name}-${file.size}-${file.lastModified}`}
              file={file}
              onRemove={() => removeFile(index)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function AttachedFileRow({
  file,
  onRemove,
}: {
  file: File;
  onRemove: () => void;
}) {
  const { base, ext } = splitFileName(file.name);
  const Icon = getFileIcon(ext);
  const meta = [ext.toUpperCase(), formatFileSize(file.size)]
    .filter(Boolean)
    .join(" · ");

  return (
    <li className="flex items-center gap-3 rounded-md border border-semantic-border-primary bg-semantic-bg-primary-alt px-3 py-2">
      <Icon
        className="h-5 w-5 flex-shrink-0 text-semantic-text-secondary"
        aria-hidden
      />
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 text-sm text-semantic-text-primary">
          <span className="truncate">{base}</span>
          {ext && <span className="flex-shrink-0">.{ext}</span>}
        </span>
        {meta && (
          <span className="block text-xs text-semantic-text-secondary">
            {meta}
          </span>
        )}
      </span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${file.name}`}
        className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded text-semantic-text-secondary hover:bg-semantic-state-active hover:text-semantic-text-primary"
      >
        <X className="h-4 w-4" aria-hidden />
      </button>
    </li>
  );
}

export default AttachmentDropzone;
